import { GridFiltersProps } from '@nexelem/table';
import clsx from 'clsx';
import { useState } from 'react';
import {
  DateRagePickerFilter,
  FiltersClearButton,
  FiltersProvider,
  SearchInput,
  SwitchFilter,
  FilterIcon,
} from '@nexelem/filters';
import { Collapse } from '@nexelem/deprecated-theme';
import { Button, Chip } from '@nexelem/components';
import { ExpandLess } from '@mui/icons-material';
import { ToDoCheckboxFilter, useFlatTasksListStore, useTreeTasksListStore } from '../../../../ToDoList';
import { useSelectAllStore } from '../../../../ToDoList/store/useSelectAllStore';
import { ProductionQueueSelect } from './ProductionQueueSelect';
import { useCustomTranslation } from '../../../../../../hooks';
import { useIsGanttSimulation } from '../../../GanttSimulation/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiagramSubtask } from '@fortawesome/pro-light-svg-icons';

type DueDate = {
  from: string;
  to: string;
};

export type DrawerFiltersData = {
  taskName: string;
  dueDate: DueDate;
  taskTypes: string[];
  showOrders: boolean;
  queues: number[];
};

export type ProductionPlanDrawerFilters = Omit<GridFiltersProps<DrawerFiltersData>, 'store' | 'exportToCsv'> & {
  toggleTreeView: () => void;
  isTreeViewEnabled: boolean;
  onToggleChecked: (value: boolean) => void;
  isAllTasksChecked: boolean;
};

export const TasksDrawerFilters = ({
  onChange,
  filters,
  toggleTreeView,
  isTreeViewEnabled,
  isAllTasksChecked,
}: ProductionPlanDrawerFilters) => {
  const { tWrapper } = useCustomTranslation('products.productionPlan.drawerFilters');
  const [areFiltersExpanded, setAreFiltersExpanded] = useState(false);
  const isSimulation = useIsGanttSimulation();

  const toggleExpandFilters = () => {
    setAreFiltersExpanded((prev) => !prev);
  };

  const selectAll = useSelectAllStore((state) => state.selectAll);
  const setSelectAll = useSelectAllStore((state) => state.setSelectAll);
  const onFlatListCheckAll = useFlatTasksListStore((state) => state.onCheckAll);
  const onTreeListCheckAll = useTreeTasksListStore((state) => state.onCheckAll);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    onFlatListCheckAll();
    onTreeListCheckAll();
  };

  return (
    <FiltersProvider value={filters} onChange={onChange}>
      {(register) => (
        <div className="flex w-full flex-col border-b border-border-primary">
          <div className={clsx(!isSimulation ? 'pl-4' : 'pl-1', 'flex items-center gap-2 pr-2 pb-2')}>
            {isSimulation && (
              <ToDoCheckboxFilter partialCheck={isAllTasksChecked} value={selectAll} onChange={handleSelectAll} />
            )}
            <SearchInput saveToSearchParams path="taskName" placeholder={tWrapper('search')} register={register} />
            <Chip
              className="ml-auto"
              label={tWrapper('treeViewButton')}
              selected={isTreeViewEnabled}
              StartIcon={<FontAwesomeIcon icon={faDiagramSubtask} />}
              onClick={toggleTreeView}
            />
            <FilterIcon onClick={toggleExpandFilters} />
          </div>
          <Collapse in={areFiltersExpanded} timeout={300}>
            <div className="flex flex-col gap-6 border-t border-border-primary bg-surface-container-lighter px-6 pt-6 pb-2">
              <DateRagePickerFilter label={tWrapper('dueDate')} path="dueDate" register={register} />
              <ProductionQueueSelect />
              <div className="flex items-center justify-between">
                <span className="text-s11-sb uppercase">{tWrapper('showOrders.label')}</span>
                <SwitchFilter
                  leftContentText={tWrapper('showOrders.on')}
                  path="showOrders"
                  register={register}
                  rightContentText={tWrapper('showOrders.off')}
                />
              </div>
              <div className="flex justify-end">
                <FiltersClearButton label={tWrapper('reset')} />
                <Button StartIcon={<ExpandLess />} variant="text" onClick={toggleExpandFilters}>
                  {tWrapper('collapse')}
                </Button>
              </div>
            </div>
          </Collapse>
        </div>
      )}
    </FiltersProvider>
  );
};
